import React from "react";
import type { SkillResult } from "../types";
import styles from "../SkillGapAssessment.module.scss";

interface SkillScoreSummaryProps {
  skills: SkillResult[];
}

function getRowColor(gap: number): "green" | "yellow" | "red" {
  if (gap <= 0) return "green";
  if (gap === 1) return "yellow";
  return "red";
}

const severityOrder: Record<string, number> = {
  significant_gap: 0,
  moderate_gap: 1,
  minor_gap: 2,
  meets_target: 3,
  exceeds_target: 4,
};

export default function SkillScoreSummary({ skills }: SkillScoreSummaryProps): JSX.Element {
  const sorted = [...skills].sort((a, b) => {
    const severityDiff =
      (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5);
    if (severityDiff !== 0) return severityDiff;
    return b.gap - a.gap;
  });

  return (
    <div className={styles.scoreSummarySection}>
      <div className={styles.scoreSummaryHeader}>
        <h3 className={styles.scoreSummaryHeading}>Skill Score Summary</h3>
        <p className={styles.scoreSummarySubtitle}>
          Achieved score vs. required target for every assessed skill
        </p>
      </div>

      <div className={styles.scoreSummaryLegend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} data-color="green" />
          Meets / Exceeds Target
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} data-color="yellow" />
          Slightly Below Target
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} data-color="red" />
          Significant Gap
        </span>
      </div>

      <table className={styles.scoreSummaryTable}>
        <thead>
          <tr>
            <th className={styles.scoreThSkill}>Skill</th>
            <th className={styles.scoreTh}>Target</th>
            <th className={styles.scoreTh}>Achieved</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((skill, i) => {
            const color = getRowColor(skill.gap);
            return (
              <tr
                key={`${skill.skill}-${i}`}
                className={`${styles.scoreRow} ${styles[`scoreRow-${color}`]}`}
              >
                <td className={styles.scoreCellSkill}>
                  <span className={styles.scoreColorDot} data-color={color} />
                  {skill.skill}
                </td>
                <td className={styles.scoreCell}>{skill.target_level}</td>
                <td className={styles.scoreCell}>{skill.current_score}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
